import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, Badge, SectionTitle } from "@/components/ui-hud";
import { MEMBERSHIP_TIERS, DIGITAL_ID_PRICE, DIGITAL_ID_LOOKUP_KEY } from "@/data/pjp";
import { Check, ArrowRight, IdCard, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { redirectExternal } from "@/lib/redirect";
import { toast } from "sonner";
import MembershipPreview from "@/components/MembershipPreview";
import BackToCockpit from "@/components/BackToCockpit";

export default function Membership() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busySlug, setBusySlug] = useState(null);

  const goCheckout = async (slug, lookup_key) => {
    if (!user) {
      // Send them to register, then come right back to trigger checkout.
      sessionStorage.setItem("pjp_pending_checkout", lookup_key);
      nav(`/register?tier=${slug}`);
      return;
    }
    // Tier picks route through the Digital ID upsell interstitial. The
    // standalone Digital ID buy (slug === "digital-id") skips it — the
    // shopper is already deliberately buying just the badge.
    if (slug !== "digital-id") {
      nav(`/membership/addon?tier=${slug}`);
      return;
    }
    setBusySlug(slug);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      if (!redirectExternal(r.data.checkout_url)) {
        setBusySlug(null);
        toast.error("Popup blocked. Please allow pop-ups and try again.");
      }
    } catch (e) {
      setBusySlug(null);
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    }
  };

  const buyDigitalId = () => goCheckout("digital-id", DIGITAL_ID_LOOKUP_KEY);

  return (
    <div>
      {/* HERO — aerial cloudscape video background */}
      <section className="relative overflow-hidden">
        {/* Background video. Muted + playsInline so iOS Safari autoplays.
            Two sources so mobile pulls the lighter 720p clip. */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/hero/clouds-poster.jpg"
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
          data-testid="hero-video"
        >
          <source src="/hero/clouds-1080.mp4" type="video/mp4" media="(min-width: 768px)" />
          <source src="/hero/clouds-720.mp4"  type="video/mp4" />
        </video>
        {/* Readability overlays — one dark gradient bottom→top so type stays crisp,
            plus a subtle right-fade so the CTA column pops. */}
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/50 via-obsidian/60 to-obsidian pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian/70 via-obsidian/20 to-transparent pointer-events-none" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28">
          {user && <BackToCockpit />}
          <div className="overline text-cyan mb-3">// PRICING · FREE FOREVER + PRO</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-none text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.6)]">
            Free to join. <span className="text-cyan">Pro when you're ready.</span>
          </h1>
          <p className="mt-5 text-slate-200 max-w-3xl text-lg drop-shadow-[0_1px_10px_rgba(0,0,0,0.6)]">
            Every crew joins free and gets a verified profile. Upgrade to <span className="text-white font-semibold">PJP Pro</span> when
            you want the Job Board, AeroIntel, JumpSeat, and every professional tool built for aviation.
          </p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {[
              { n: "26,511+", l: "MEMBERS" },
              { n: "$0", l: "TO JOIN" },
              { n: "$129", l: "PRO · PER YEAR" },
              { n: "$199", l: "DIGITAL ID · PER YEAR" },
            ].map((s, i) => (
              <div key={i} className="border-l border-cyan/40 pl-3">
                <div className="font-mono text-2xl text-cyan drop-shadow-[0_1px_10px_rgba(0,0,0,0.7)]">{s.n}</div>
                <div className="overline mt-1 text-white/70">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Anonymous-visitor preview — see the network before you pay */}
      <MembershipPreview />

      {/* FREE-vs-PRO comparison card — this is the primary decision surface for crew */}
      <section className="max-w-7xl mx-auto px-6 pt-4 pb-8">
        <div className="grid md:grid-cols-2 gap-5">
          {/* FREE TIER */}
          <Card testId="tier-free" className="!p-6">
            <div className="overline text-white/60 mb-2">// COMMUNITY</div>
            <div className="font-display text-2xl font-bold tracking-tight mb-1">PJP Free</div>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="font-mono text-5xl text-white">$0</span>
              <span className="text-xs text-slate-400">forever</span>
            </div>
            <p className="text-sm text-slate-400 mb-5">Verified profile in the world's largest private-aviation community.</p>
            <ul className="space-y-2 mb-6">
              {[
                "Verified crew profile with FAA (or manual FA) verification",
                "Browse Hot Mic — the private aviation feed",
                "Browse Crew Directory (names + city)",
                "3 free AeroIntel queries per day",
                "Digital-only Digital ID preview",
              ].map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="h-3.5 w-3.5 text-white/60 mt-1 flex-shrink-0" />
                  <span className="text-slate-300">{p}</span>
                </li>
              ))}
            </ul>
            {user ? (
              <div className="mono text-[10px] tracking-widest uppercase text-white/50 flex items-center gap-1.5">
                <Check className="h-3 w-3 text-avail" /> You're on Free — upgrade below to unlock Pro
              </div>
            ) : (
              <Link to="/register" data-testid="btn-join-free" className="btn-hud w-full justify-center">
                JOIN FREE <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </Card>

          {/* PRO TIER — featured */}
          <Card testId="tier-pro" className="!p-6 !border-cyan/50 relative shadow-[0_0_40px_-14px_rgba(75,156,255,0.5)]">
            <div className="absolute -top-3 left-5"><Badge tone="cyan" pulse>MOST POPULAR</Badge></div>
            <div className="overline text-cyan mb-2">// ALL CREW · UNLOCKED</div>
            <div className="font-display text-2xl font-bold tracking-tight mb-1">PJP Pro</div>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="font-mono text-5xl text-cyan glow-cyan">$129</span>
              <span className="text-xs text-slate-400">/ year <span className="text-white/40">· $10.75/mo</span></span>
            </div>
            <p className="text-sm text-slate-400 mb-5">One membership. Every professional tool. Every crew role.</p>
            <ul className="space-y-2 mb-6">
              {[
                "Job Board — apply, save, and get alerts",
                "AeroIntel — unlimited aviation AI",
                "FREE JumpSeat access ($10K/yr rider value)",
                "Direct messaging with pilots, ops, and operators",
                "Full Crew Directory contact info",
                "Fleet Live Map + Community Docs + Salary Insights",
                "Unlimited Hot Mic posts + reactions",
              ].map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="h-3.5 w-3.5 text-cyan mt-1 flex-shrink-0" />
                  <span className="text-slate-300">{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto border-t border-cyan/10 pt-4 mb-4">
              <Link
                to="/digital-id/demo"
                data-testid="badge-preview-pro"
                className="flex items-start gap-2 p-2 -m-2 rounded-lg hover:bg-orange-hud/[0.06] transition-colors group"
              >
                <IdCard className="h-4 w-4 text-orange-hud mt-0.5 group-hover:scale-110 transition-transform" />
                <div className="flex-1">
                  <div className="font-mono text-xs text-orange-hud flex items-center gap-1">
                    + DIGITAL ID BADGE · ${DIGITAL_ID_PRICE}/YR
                    <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="text-xs text-slate-400 mt-1">Add the physical NFC badge at checkout — wallet-ready.</div>
                </div>
              </Link>
            </div>
            <button
              onClick={() => goCheckout("pro", "pjp_pro_yearly")}
              disabled={busySlug === "pro"}
              data-testid="btn-join-pro"
              className="btn-hud btn-hud-solid w-full justify-center"
            >
              {busySlug === "pro" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>UPGRADE TO PRO — $129/YR <ArrowRight className="h-3.5 w-3.5" /></>}
            </button>
          </Card>
        </div>
      </section>

      {/* Operator / Client / higher tiers */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="overline text-cyan/70 mb-3">// SPECIALIZED TIERS</div>
        <h2 className="font-display text-3xl font-bold tracking-tight mb-6">For operators and charter clients.</h2>
        <div className="grid md:grid-cols-2 gap-5 stagger">
          {MEMBERSHIP_TIERS.filter((t) => t.slug !== "pro").map((t) => {
            const busy = busySlug === t.slug;
            return (
              <Card key={t.slug} testId={`tier-${t.slug}`} className={t.featured ? "border-cyan/50 relative" : ""}>
                {t.featured && (
                  <div className="absolute -top-3 left-5">
                    <Badge tone="orange" pulse>OPERATORS</Badge>
                  </div>
                )}
                <div className="overline text-cyan mb-2">{t.kicker}</div>
                <div className="font-display text-2xl font-bold tracking-tight mb-1">{t.label}</div>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="font-mono text-4xl text-cyan glow-cyan">${t.price.toLocaleString()}</span>
                  <span className="text-xs text-slate-400">/ {t.billing === "yearly" ? "year" : "month"}</span>
                </div>
                <p className="text-sm text-slate-400 mb-5">{t.tag}</p>
                <ul className="space-y-2 mb-6">
                  {t.perks.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="h-3.5 w-3.5 text-cyan mt-1 flex-shrink-0" />
                      <span className="text-slate-300">{p}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => goCheckout(t.slug, t.lookup_key)}
                  disabled={busy}
                  data-testid={`btn-join-${t.slug}`}
                  className="btn-hud btn-hud-solid w-full justify-center"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>JOIN AS {t.label.toUpperCase()} <ArrowRight className="h-3.5 w-3.5" /></>}
                </button>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="mt-2">
          <Card className="border-orange-hud/40">
            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="overline text-orange-hud mb-2">ADD-ON · ANNUAL</div>
                <div className="font-display text-2xl font-bold tracking-tight mb-1">PJP Digital ID · $199<span className="text-white/50 text-base font-normal">/year</span></div>
                <p className="text-sm text-slate-400 max-w-2xl">
                  Wallet-ready, NFC-scannable, FAA cross-checked verified badge. Neon-green verified banner,
                  CREW marquee, QR code. Ships to any active PJP member — pair it with any tier.
                </p>
              </div>
              <button
                onClick={buyDigitalId}
                disabled={busySlug === "digital-id"}
                data-testid="btn-buy-digital-id"
                className="btn-hud btn-hud-solid"
              >
                {busySlug === "digital-id" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>BUY DIGITAL ID <ArrowRight className="h-3.5 w-3.5" /></>}
              </button>
            </div>
          </Card>
        </div>

        <p className="mt-8 text-xs text-slate-500 text-center font-mono">
          PJP Free forever. PJP Pro is $129/year — that's less than $11/month. Digital ID Badge add-on is $199/year.
          Charter Operator $299/month. Charter Client $5,000/year. Secure Stripe checkout · Cancel anytime.
        </p>
      </section>

      <section className="border-y border-cyan/10 bg-obsidian/60 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <SectionTitle overline="MEMBER BENEFITS" title="Everything included in your membership." subtitle="PJP is built for working aviation professionals. Your membership gives you direct access to the people, opportunities, and resources that move the private aviation industry." />
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { t: "Verified Member Directory", d: "Browse and connect with 26,500+ verified aviation professionals across every role in private aviation." },
              { t: "PJP Facebook Community", d: "Access the private PJP Facebook group — the most active private aviation professional network online." },
              { t: "Job Board & Opportunities", d: "Exclusive contract postings, full-time roles, and charter opportunities shared only with PJP members." },
              { t: "Digital ID Badge", d: "FAA-verified pilots can add a shareable Digital ID badge for $199/year — wallet-ready, linkable from your profile, and accepted as professional verification." },
              { t: "Professional Profile", d: "Build a verified profile with your ratings, type certificates, experience, and availability for the industry to find you." },
              { t: "AeroIntel — AI + Community", d: "AI-powered aviation intelligence enhanced by 26,500+ pilots. Regulations, aircraft systems, airports, careers, maintenance, and industry insights." },
            ].map((b) => (
              <Card key={b.t}>
                <div className="font-display text-xl font-semibold tracking-tight mb-2">{b.t}</div>
                <p className="text-sm text-slate-400">{b.d}</p>
              </Card>
            ))}
          </div>
          <div className="mt-10 text-center">
            <div className="overline text-cyan mb-3">QUESTIONS ABOUT MEMBERSHIP?</div>
            <Link to="/contact" data-testid="link-contact-membership" className="btn-hud">CONTACT US <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
